import { useCallback, useEffect, useState } from "react";

import { toast } from "sonner";

import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { InviteProgramParticipantsDialog } from "@/components/pages_logged_in/program/components/program-participants/InviteProgramParticipantsDialog";
import { AddNewUsersCallout } from "@/components/pages_logged_in/workspace-admin/AddNewUsersCallout";
import { Button } from "@/components/ui/button";
import {
  ProgramComponentsQueryDocument,
  ProgramQueryDocument,
  ProgramUserRole,
  useRemoveProgramLeadMutation,
  useWorkspaceCreditsQuery,
  useWorkspaceInvitationBatchesForProgramStatusQuery,
  WorkspaceInvitationBatchesStatus,
} from "@/graphql";
import { MainProgram } from "@/types/programs";

import { LeadItem } from "./LeadItem";

const POLLING_INTERVAL_IN_MILLISECONDS = 1500;

interface ProgramLeadsProps {
  program: MainProgram;
  isLead: boolean;
  isClosed: boolean;
  showModification: boolean;
  refetchProgram: () => void;
}

export function ProgramLeads({
  program,
  isLead,
  isClosed,
  showModification,
  refetchProgram,
}: ProgramLeadsProps) {
  const [isOpenInvitationDialog, setIsOpenInvitationDialog] = useState(false);
  const isAdmin = isWorkspaceAdmin();
  const programId = program.id;
  const programLeads = program.programOwners;

  const {
    data: invitationBatchesStatusData,
    startPolling: startPollingInvitationBatchesStatus,
    stopPolling: stopPollingInvitationBatchesStatus,
  } = useWorkspaceInvitationBatchesForProgramStatusQuery({
    notifyOnNetworkStatusChange: true,
    variables: { programId },
    skip: !programId || !isLead,
    onCompleted: (data) => {
      const hasAnyRunningInvitationBatch =
        data?.workspaceInvitationBatchesForProgramStatus ===
        WorkspaceInvitationBatchesStatus.InProgress;
      if (!hasAnyRunningInvitationBatch) {
        stopPollingInvitationBatchesStatus();
        refetchProgram();
      }
    },
    onError: () => {
      stopPollingInvitationBatchesStatus();
    },
  });

  useEffect(() => {
    startPollingInvitationBatchesStatus(POLLING_INTERVAL_IN_MILLISECONDS);
    return () => {
      stopPollingInvitationBatchesStatus();
    };
  }, [startPollingInvitationBatchesStatus, stopPollingInvitationBatchesStatus]);

  const removeErrorHandler = (error?: string) => {
    toast.error(error || "Cannot remove program lead");
  };

  const [removeProgramLead, { loading: removing }] =
    useRemoveProgramLeadMutation({
      onCompleted: (data) => {
        const { msg, ok } = data?.removeProgramLead || {};
        if (ok) {
          toast.success("Program Lead removed successfully");
        } else {
          removeErrorHandler(msg ?? undefined);
        }
      },
      onError: (err) => {
        removeErrorHandler(err?.message);
      },
      refetchQueries: [
        {
          query: ProgramComponentsQueryDocument,
          variables: {
            programId,
          },
        },
        {
          query: ProgramQueryDocument,
          variables: {
            programId,
          },
        },
      ],
    });

  const { data: workspaceCreditsData } = useWorkspaceCreditsQuery({
    skip: !isWorkspaceAdmin(),
  });

  const { currentWorkspaceCredits } = workspaceCreditsData || {
    currentWorkspaceCredits: "0",
    currentWorkspace: null,
  };

  const onRemoveProgramLead = useCallback(
    (userId: string) => {
      removeProgramLead({ variables: { programId, programUserId: userId } });
    },
    [programId, removeProgramLead],
  );

  const handleInvitationDialogClose = useCallback(() => {
    setIsOpenInvitationDialog(false);
  }, []);

  const handleInvitationDialogSuccess = useCallback(() => {
    setIsOpenInvitationDialog(false);
    startPollingInvitationBatchesStatus(POLLING_INTERVAL_IN_MILLISECONDS);
  }, [refetchProgram]);

  const invitationBatchesStatus =
    invitationBatchesStatusData?.workspaceInvitationBatchesForProgramStatus;

  const hasAnyRunningInvitationBatch =
    invitationBatchesStatus === WorkspaceInvitationBatchesStatus.InProgress;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold">Program Leads</div>
        {isAdmin && !isClosed && showModification && (
          <Button
            intent="secondary"
            size="sm"
            onClick={() => setIsOpenInvitationDialog(true)}
          >
            + Add
          </Button>
        )}
      </div>

      {programLeads.map((item) => (
        <LeadItem
          key={item.id}
          data={item}
          isAdmin={isAdmin}
          allowRemove={!isClosed && showModification && programLeads.length > 1}
          onRemoveProgramLead={() => onRemoveProgramLead(item.id)}
          enableEmail={!isLead}
          loading={removing}
        />
      ))}

      {hasAnyRunningInvitationBatch && (
        <div className="my-3">
          <AddNewUsersCallout
            status={invitationBatchesStatus}
            isProgram={true}
          />
        </div>
      )}

      <InviteProgramParticipantsDialog
        open={isOpenInvitationDialog}
        afterAddOrUpdateSuccess={handleInvitationDialogSuccess}
        onClose={handleInvitationDialogClose}
        program={program}
        role={ProgramUserRole.Lead}
        title="Add Program Lead"
        buttonText="Add Program Lead"
        currentWorkspaceCredits={currentWorkspaceCredits}
      />
    </div>
  );
}
